/**
 * Shared settings plumbing for the list sidebars (fax, chat, submission, completed
 * session and task lists). Each sidebar keeps its own fields and storage key; these
 * are the parts that must behave the same in every product:
 *
 * - "Changed from default" has one definition (`isCategoryActive`), used by the
 *   SidebarSettingsMenu labels and Clear all, every sidebar's filter-icon highlight,
 *   and each sidebar's decision of what to save.
 * - Display column sets compare as sets (`sameMembers`). Toggling appends and removes,
 *   so order means nothing — and a count check misses a same-size swap (Tag off +
 *   Patient Name on), which then silently never persists.
 * - Saved settings go through `readSavedFilters` / `writeSavedFilters`, so unreadable,
 *   corrupt or full storage never breaks the list or blocks a filter change.
 * - Saved ids are checked against the option list they came from (`keepKnownIds`)
 *   whenever that list (re)loads.
 */

export function sameMembers(a, b) {
  if (!Array.isArray(a) || !Array.isArray(b)) throw new Error('sameMembers: both values must be arrays');
  return a.length === b.length && a.every(v => b.includes(v)) && b.every(v => a.includes(v));
}

/**
 * Whether a SidebarSettingsMenu category differs from its default — or, for a category
 * without one, has a selection.
 */
export function isCategoryActive(cat) {
  if (cat.type === 'radio') {
    if (cat.defaultValue !== undefined) return cat.value !== cat.defaultValue;
    return !!cat.value;
  }
  if (cat.type === 'checkbox') {
    if (cat.defaultValue !== undefined) return !sameMembers(cat.value, cat.defaultValue);
    return Array.isArray(cat.value) && cat.value.length > 0;
  }
  return false;
}

/** A list's saved settings, or {} when nothing usable is saved. */
export function readSavedFilters(key) {
  try {
    const saved = JSON.parse(localStorage.getItem(key));
    return saved && typeof saved === 'object' && !Array.isArray(saved) ? saved : {};
  } catch {
    return {};
  }
}

/**
 * Saves a list's non-default settings; with nothing left the key is removed. Storage
 * that is full or blocked must not stop the filter change itself from applying.
 */
export function writeSavedFilters(key, data) {
  try {
    if (Object.keys(data).length === 0) localStorage.removeItem(key);
    else localStorage.setItem(key, JSON.stringify(data));
  } catch (err) {
    console.error('Failed to save list settings', err);
  }
}

/**
 * The saved ids a freshly (re)loaded option list still has. Anything else — a deleted
 * tag or form, a removed number, a deactivated user, another account's ids after an
 * impersonation switch — would keep filtering the list while showing nowhere in the menu.
 */
export function keepKnownIds(ids, knownIds) {
  if (!Array.isArray(ids) || !Array.isArray(knownIds)) throw new Error('keepKnownIds: both values must be arrays');
  const known = new Set(knownIds);
  return ids.filter(id => known.has(id));
}
